import { swSettings } from "app/common/services/swSettings";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { proNavigator } from "single-spa/react-app/ProNavigator";

/**
 * Resolves the appropriate country for navigation to a given state.
 * This utility handles country validation logic to ensure the target component
 * supports the resolved country.
 *
 * @param stateName - The name of the state to navigate to
 * @param preferredCountry - The preferred country ID to use if allowed
 * @returns The resolved country ID that is allowed for the target state
 * @throws Error if state or component configuration is invalid
 */
export const resolveAllowedCountry = (stateName: string, preferredCountry?: number): number | undefined => {
    const state = proNavigator.getState(stateName);
    const componentId = state?.configId;

    if (!state || !componentId) {
        throw new Error(`Invalid state configuration for ${stateName}`);
    }

    const resolvedComponentId = typeof componentId === "function" ? componentId(state, {}) : componentId;
    const toComponent = swSettings.components[resolvedComponentId];
    const defaultParams = state.defaultQueryParams;
    const defaultCountry = defaultParams?.country ?? WORLDWIDE_COUNTRY_ID;
    let resolvedCountry = toComponent.resources?.Countries[0];

    // Priority order for country resolution:
    // 1. If preferred country is provided and allowed, use it
    // 2. If default country is allowed, use it
    // 3. Fall back to first available country from component resources
    if (preferredCountry && swSettings.allowedCountry(preferredCountry, resolvedComponentId)) {
        resolvedCountry = preferredCountry;
    } else if (swSettings.allowedCountry(defaultCountry, resolvedComponentId)) {
        resolvedCountry = defaultCountry;
    }

    return resolvedCountry;
};

export const parseCountryParam = (countryParam: unknown): number | undefined => {
    if (countryParam === undefined || countryParam === null || countryParam === "") {
        return undefined;
    }

    const parsed = Number(countryParam);
    return Number.isFinite(parsed) ? parsed : undefined;
};

export const resolveAllowedCountryFromParam = (
    stateName: string,
    countryParam: unknown,
): number | undefined => {
    const preferredCountry = parseCountryParam(countryParam);
    return resolveAllowedCountry(stateName, preferredCountry);
};
